/**
 * BillAttachmentsViewer — read-through bill image surface.
 *
 * Architecture invariants:
 *   • Frontend NEVER sees the EC service token; all calls go through the
 *     Accounts proxy at /api/accounts/bills/{bill_id}/attachments.
 *   • No bytes cached locally — each thumbnail and full-size image is a
 *     fresh stream from EC via Accounts.
 *   • Multi-page bills render one thumbnail per page; clicking opens a
 *     full-size modal with page tabs.
 *
 * States:
 *   loading      — spinner
 *   has_pages    — thumbnail grid + click-to-zoom
 *   no_image     — "No bill image available"
 *   unavailable  — "Bill image temporarily unavailable" (EC 503 / network)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, AlertTriangle, Loader2, X } from 'lucide-react';
import { accountsAPI } from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const buildAuthHeaders = () => {
  const t = localStorage.getItem('token') || sessionStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/** Hook that resolves an Accounts proxy URL into an `objectURL` blob.
 *  Critical: an HTML <img src> can't carry a bearer header, so we fetch
 *  the bytes ourselves and convert to a blob URL the browser can render.
 *
 *  Returns { src, error, errorCode, retry } — `errorCode` maps to the
 *  structured detail codes emitted by the Accounts proxy at
 *  /api/accounts/bills/{id}/attachments/{id}/view (see server.py):
 *    EC_ATTACHMENT_NOT_FOUND / EC_STORAGE_ERROR / EC_UNREACHABLE /
 *    EC_AUTH_REJECTED / EC_NOT_CONFIGURED.
 */
const useAuthedImage = (path) => {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!path) return undefined;
    let alive = true;
    let blobUrl = null;
    setError(null);
    setErrorCode(null);
    setSrc(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}${path}`, { headers: buildAuthHeaders() });
        if (!r.ok) {
          // Try to read structured detail from the Accounts proxy.
          let code = null;
          let msg = `HTTP ${r.status}`;
          try {
            const body = await r.clone().json();
            if (body?.detail) {
              if (typeof body.detail === 'string') {
                msg = body.detail;
              } else {
                code = body.detail.code || null;
                msg = body.detail.message || body.detail.reason || msg;
              }
            }
          } catch (_e) { /* body wasn't JSON, fine */ }
          const err = new Error(msg);
          err.code = code;
          err.httpStatus = r.status;
          throw err;
        }
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        if (alive) setSrc(blobUrl);
      } catch (e) {
        if (alive) {
          setError(e?.message || 'load_failed');
          setErrorCode(e?.code || null);
        }
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path, attempt]);
  const retry = () => setAttempt((a) => a + 1);
  return { src, error, errorCode, retry };
};

// Human copy per structured code — keeps the "retry" pill sensible even
// when EC is definitively wrong (missing / auth) vs merely flaky (5xx /
// unreachable).
const ERROR_COPY = {
  EC_ATTACHMENT_NOT_FOUND: { label: 'Attachment missing', retryable: false },
  EC_STORAGE_ERROR:        { label: 'Attachment unavailable', retryable: true },
  EC_UNREACHABLE:          { label: 'Attachment unavailable', retryable: true },
  EC_AUTH_REJECTED:        { label: 'Attachment blocked', retryable: false },
  EC_NOT_CONFIGURED:       { label: 'Attachment source offline', retryable: false },
};
const errorLabel = (code) => (ERROR_COPY[code] || { label: 'Attachment unavailable', retryable: true });

const PageThumb = ({ billId, attachment, onClick }) => {
  const { src, error, errorCode, retry } = useAuthedImage(attachment.view_url);
  const errInfo = errorLabel(errorCode);
  return (
    <div
      className="group relative w-24 h-32 rounded-md border border-zinc-700/70 bg-zinc-900/40 overflow-hidden hover:border-emerald-400/70 transition-colors"
      data-testid={`bill-attachment-thumb-${billId}-${attachment.attachment_id}-p${attachment.page_number}`}
    >
      {!src && !error && (
        <div className="absolute inset-0 grid place-items-center text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
      {error && (
        <div
          className="absolute inset-0 grid place-items-center text-zinc-500 text-[10px] px-1 text-center gap-1"
          data-testid={`bill-attachment-thumb-error-${attachment.attachment_id}`}
        >
          <ImageIcon className="w-4 h-4" />
          <span>{errInfo.label}</span>
          {errInfo.retryable && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); retry(); }}
              className="text-[10px] text-amber-300 hover:text-amber-200 underline"
              data-testid={`bill-attachment-thumb-retry-${attachment.attachment_id}`}
            >
              retry
            </button>
          )}
        </div>
      )}
      {src && (
        <button
          type="button"
          onClick={onClick}
          className="w-full h-full"
          aria-label={`Open page ${attachment.page_number}`}
        >
          <img
            src={src}
            alt={`Bill page ${attachment.page_number}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        </button>
      )}
      <span className="absolute bottom-1 right-1 text-[10px] bg-black/70 text-white rounded px-1.5 py-0.5">
        p.{attachment.page_number}
      </span>
    </div>
  );
};

const FullPageView = ({ attachment }) => {
  const { src, error, errorCode, retry } = useAuthedImage(attachment.view_url);
  if (error) {
    const errInfo = errorLabel(errorCode);
    return (
      <div
        className="grid place-items-center h-full text-zinc-400 gap-2 py-12"
        data-testid={`bill-attachment-full-error-${attachment.page_number}`}
      >
        <ImageIcon className="w-8 h-8 text-zinc-500" />
        <span className="text-sm">{errInfo.label}</span>
        <span className="text-[11px] text-zinc-500">
          Page {attachment.page_number}
          {errorCode ? ` · ${errorCode}` : ''}
        </span>
        {errInfo.retryable && (
          <button
            type="button"
            onClick={retry}
            className="mt-2 text-xs px-3 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            data-testid={`bill-attachment-full-retry-${attachment.page_number}`}
          >
            Retry
          </button>
        )}
      </div>
    );
  }
  if (!src) {
    return (
      <div className="grid place-items-center h-full text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={`Bill page ${attachment.page_number}`}
      className="max-h-[80vh] max-w-full mx-auto rounded-md"
      data-testid={`bill-attachment-full-${attachment.page_number}`}
    />
  );
};

const Modal = ({ pages, initialIndex, onClose }) => {
  const [idx, setIdx] = useState(initialIndex || 0);
  const total = pages.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && idx < total - 1) setIdx((i) => i + 1);
      if (e.key === 'ArrowLeft' && idx > 0) setIdx((i) => i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, total, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-6"
      data-testid="bill-attachment-modal"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950/90 border border-zinc-800 rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/70">
          <div className="flex items-center gap-2 flex-wrap">
            {total > 1 &&
              pages.map((p, i) => (
                <button
                  key={`${p.attachment_id}-p${p.page_number}`}
                  type="button"
                  data-testid={`bill-attachment-tab-${p.attachment_id}-p${p.page_number}`}
                  onClick={() => setIdx(i)}
                  className={`px-2.5 py-1 rounded text-xs transition-colors ${
                    i === idx
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                      : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:text-zinc-200'
                  }`}
                >
                  Page {p.page_number}
                </button>
              ))}
            {total === 1 && (
              <span className="text-xs text-zinc-400">Single page bill</span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bill-attachment-modal-close"
            aria-label="Close"
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(90vh - 56px)' }}>
          <FullPageView attachment={pages[idx]} />
        </div>
      </div>
    </div>
  );
};

const BillAttachmentsViewer = ({ billId }) => {
  const [state, setState] = useState({ kind: 'loading' });
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!billId) return undefined;
    setState({ kind: 'loading' });
    (async () => {
      try {
        const res = await accountsAPI.getBillAttachments(billId);
        if (!alive) return;
        const list = (res?.data?.attachments) || [];
        setState({ kind: list.length === 0 ? 'no_image' : 'has_pages', pages: list });
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 404) {
          setState({ kind: 'no_image' });
        } else {
          setState({ kind: 'unavailable' });
        }
      }
    })();
    return () => { alive = false; };
  }, [billId]);

  const pages = useMemo(
    () => (state.pages || []).slice().sort((a, b) => a.page_number - b.page_number),
    [state.pages]
  );

  if (state.kind === 'loading') {
    return (
      <div
        className="flex items-center gap-2 text-zinc-400 text-sm"
        data-testid="bill-attachments-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading bill image…
      </div>
    );
  }

  if (state.kind === 'no_image') {
    return (
      <div
        className="flex items-center gap-2 text-zinc-500 text-sm border border-zinc-800/70 bg-zinc-900/30 rounded-md px-3 py-2"
        data-testid="bill-attachments-empty"
      >
        <ImageIcon className="w-4 h-4" />
        No bill image available
      </div>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <div
        className="flex items-center gap-2 text-amber-400 text-sm border border-amber-500/30 bg-amber-500/5 rounded-md px-3 py-2"
        data-testid="bill-attachments-unavailable"
      >
        <AlertTriangle className="w-4 h-4" />
        Bill image temporarily unavailable
      </div>
    );
  }

  return (
    <div data-testid="bill-attachments-viewer">
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-zinc-500">
        <ImageIcon className="w-3.5 h-3.5" />
        {pages.length === 1 ? 'Bill image' : `Bill image · ${pages.length} pages`}
      </div>
      <div className="flex flex-wrap gap-2">
        {pages.map((p, i) => (
          <PageThumb
            key={`${billId}-${p.attachment_id}-p${p.page_number}`}
            billId={billId}
            attachment={p}
            onClick={() => setOpenIdx(i)}
          />
        ))}
      </div>
      {openIdx !== null && (
        <Modal pages={pages} initialIndex={openIdx} onClose={() => setOpenIdx(null)} />
      )}
    </div>
  );
};

/**
 * BillRowThumb — tiny inline thumbnail for the bills list row.
 * Lazy-fetches the first attachment via the Accounts proxy.
 * Renders one of: spinner, image (with optional "+N" badge for multi-page),
 * dash for "no image", or amber dot for "unavailable".
 */
export const BillRowThumb = ({ billId }) => {
  const [state, setState] = useState({ kind: 'loading' });
  useEffect(() => {
    let alive = true;
    if (!billId) return undefined;
    (async () => {
      try {
        const res = await accountsAPI.getBillAttachments(billId);
        if (!alive) return;
        const list = (res?.data?.attachments) || [];
        if (list.length === 0) setState({ kind: 'no_image' });
        else setState({ kind: 'has', first: list[0], total: list.length });
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        setState({ kind: status === 404 ? 'no_image' : 'unavailable' });
      }
    })();
    return () => { alive = false; };
  }, [billId]);

  if (state.kind === 'loading') {
    return (
      <span
        className="inline-flex w-10 h-12 rounded border border-zinc-800/70 bg-zinc-900/40 items-center justify-center"
        data-testid={`bill-row-thumb-${billId}-loading`}
      >
        <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />
      </span>
    );
  }
  if (state.kind === 'no_image') {
    return (
      <span
        className="inline-flex w-10 h-12 rounded border border-dashed border-zinc-800/70 bg-zinc-900/30 items-center justify-center text-zinc-600"
        title="No bill image"
        data-testid={`bill-row-thumb-${billId}-empty`}
      >
        —
      </span>
    );
  }
  if (state.kind === 'unavailable') {
    return (
      <span
        className="inline-flex w-10 h-12 rounded border border-amber-500/30 bg-amber-500/5 items-center justify-center text-amber-400"
        title="Bill image temporarily unavailable"
        data-testid={`bill-row-thumb-${billId}-unavailable`}
      >
        <AlertTriangle className="w-3.5 h-3.5" />
      </span>
    );
  }
  return <RowThumbImage state={state} billId={billId} />;
};

const RowThumbImage = ({ state, billId }) => {
  const { src, error } = useAuthedImage(state.first.view_url);
  if (error) {
    return (
      <span
        className="inline-flex w-10 h-12 rounded border border-zinc-800/70 bg-zinc-900/40 items-center justify-center text-zinc-500"
        title="Image unavailable"
        data-testid={`bill-row-thumb-${billId}-error`}
      >
        <ImageIcon className="w-3.5 h-3.5" />
      </span>
    );
  }
  return (
    <span
      className="relative inline-flex w-10 h-12 rounded border border-zinc-700/60 overflow-hidden bg-zinc-900"
      data-testid={`bill-row-thumb-${billId}`}
    >
      {!src ? (
        <Loader2 className="w-3 h-3 animate-spin text-zinc-500 m-auto" />
      ) : (
        <img src={src} alt="bill" className="w-full h-full object-cover" />
      )}
      {state.total > 1 && (
        <span className="absolute bottom-0 right-0 text-[8px] bg-black/70 text-white px-1 rounded-tl">
          +{state.total - 1}
        </span>
      )}
    </span>
  );
};

export default BillAttachmentsViewer;
